import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { describeAxiosError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTaxYear } from "@/context/TaxYearContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { TaxYearSelect } from "@/components/TaxYearSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, DownloadCloud, ShieldCheck, FileText, User, Calculator, Send, CreditCard, Link2Off, Lock, ExternalLink, ReceiptIndianRupee, Landmark, Bell, Copy, Info } from "lucide-react";
import { toast } from "sonner";

function Card({ title, icon: Icon, right, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white rounded" data-testid={testid}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && <div className="w-8 h-8 rounded bg-gray-50 text-gray-600 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>}
          <h3 className="font-semibold text-sm text-gray-900 truncate">{title}</h3>
        </div>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Row({ k, v, strong }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 text-sm">
      <span className="text-gray-600">{k}</span>
      <span className={`font-mono ${strong ? "font-semibold text-gray-900" : "font-medium text-gray-800"}`}>{v}</span>
    </div>
  );
}

const COMP_ROWS = [
  ["total_taxable_salary", "Salary", true],
  ["total_taxable_house_property", "House property", true],
  ["total_taxable_capital_gains", "Capital gains", true],
  ["total_taxable_business_profession", "Business & profession", true],
  ["total_taxable_other", "Other sources", true],
  ["gross_total_income", "Gross total income", true],
  ["total_chapter_vi_a_deduction", "Chapter VI-A deductions", true],
  ["total_income", "Total income", true],
  ["tax_payable_on_total_income", "Tax on total income", true],
  ["rebate", "Rebate u/s 87A", true],
  ["surcharge", "Surcharge", true],
  ["cess", "Health & education cess", true],
  ["gross_tax_payable", "Gross tax payable", true],
  ["interest_234_a", "Interest u/s 234A", true],
  ["interest_234_b", "Interest u/s 234B", true],
  ["interest_234_c", "Interest u/s 234C", true],
  ["fees_234_f", "Late fee u/s 234F", true],
  ["net_tax_payable", "Net tax payable", true],
  ["total_tax_deducted_at_source", "TDS", true],
  ["total_advance_tax_paid", "Advance tax paid", true],
  ["total_self_assessment_tax", "Self-assessment tax", true],
  ["total_taxes_paid", "Total taxes paid", true],
  ["refund", "Refund", true],
  ["due", "Balance due", true],
];

const dt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const titleCase = (s) => (s ? String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

function Inner() {
  const { activeId } = useCompany();
  const { ay } = useTaxYear();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [txn, setTxn] = useState(null);        // DEMO consent transaction only
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState("");         // "start" | "verify" | "fetch" | "file" | "pay"
  const [payAmt, setPayAmt] = useState("");
  const [payKind, setPayKind] = useState("Advance Tax");
  const handledQuicko = useRef(false);
  const quickoPopup = useRef(null);

  const load = async () => {
    if (!activeId) return;
    try {
      const [s, snap] = await Promise.all([
        api.get(`/companies/${activeId}/tax/portal/status?ay=${ay}`),
        api.get(`/companies/${activeId}/tax/portal/snapshot?ay=${ay}`),
      ]);
      setStatus(s.data);
      setSnapshot(snap.data || null);
    } catch (err) {
      toast.error(describeAxiosError(err));
    }
  };

  const applyQuickoResult = async (q, reason) => {
    if (q === "connected") {
      toast.success("Income-tax portal connected via Quicko — fetching your details…");
      await fetchDetails();
    } else if (q === "cancelled") {
      toast.info("Connection cancelled on Quicko.");
      await load();
    } else if (q === "invalid_state") {
      toast.error("Connection link expired — please start again.");
      await load();
    } else if (reason === "exchange") {
      toast.error("Quicko approved the login but rejected the token exchange. Please retry; if it persists, check that your Quicko app is active and its live key/secret match.");
      await load();
    } else if (reason === "session") {
      toast.error("Your session expired during sign-in. Please click Connect and complete it in one go.");
      await load();
    } else if (reason === "rejected") {
      toast.error("Quicko did not approve the request. Confirm your app is approved and this app's exact redirect URL is whitelisted (see the panel below).");
      await load();
    } else {
      toast.error("Could not connect to Quicko. Please try again.");
      await load();
    }
  };

  useEffect(() => { load(); }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Full-page fallback: if the OAuth came back as a top-level redirect (?quicko=... in URL)
  useEffect(() => {
    if (!activeId || handledQuicko.current) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get("quicko");
    if (!q) return;
    handledQuicko.current = true;
    window.history.replaceState({}, "", window.location.pathname);
    applyQuickoResult(q, params.get("reason"));
  }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Primary path: popup OAuth result via postMessage (main window never reloads → company stays)
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (!d || d.source !== "quicko-connect") return;
      try { quickoPopup.current && quickoPopup.current.close(); } catch (_) {}
      quickoPopup.current = null;
      applyQuickoResult(d.result, d.reason);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [activeId, ay]); // eslint-disable-line react-hooks/exhaustive-deps

  const startConsent = async () => {
    setBusy("start");
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/portal/consent/start`, { ay });
      if (data.redirect_url) {
        const w = 480, h = 720;
        const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
        const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
        const popup = window.open(data.redirect_url, "quicko_oauth", `width=${w},height=${h},left=${left},top=${top}`);
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          window.location.href = data.redirect_url; // popup blocked → full-page fallback
          return;
        }
        quickoPopup.current = popup;
        try { popup.focus(); } catch (_) {}
        toast.info("Complete the OTP in the Quicko window — this page will update automatically.");
        return;
      }
      // DEMO
      setTxn(data.transaction_id);
      toast.info(data.message || "OTP sent to the registered mobile & email");
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setBusy(""); }
  };

  const verifyOtp = async () => {
    if (!/^[0-9]{4,8}$/.test(otp)) { toast.error("Enter the OTP (4-8 digits)"); return; }
    setBusy("verify");
    try {
      await api.post(`/companies/${activeId}/tax/portal/consent/verify-otp`, { transaction_id: txn, otp });
      toast.success("Consent granted — fetching your details…");
      setTxn(null); setOtp("");
      await load();
      await fetchDetails();
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setBusy(""); }
  };

  const fetchDetails = async () => {
    setBusy("fetch");
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/portal/fetch`, { ay });
      setSnapshot(data);
      toast.success(`Details fetched for AY ${ay}`);
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setBusy(""); }
  };

  const copyRedirect = async () => {
    try { await navigator.clipboard.writeText(status?.redirect_uri || ""); toast.success("Redirect URL copied"); }
    catch { toast.error("Copy failed — select and copy the URL manually"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the income-tax portal for this company?")) return;
    try {
      await api.post(`/companies/${activeId}/tax/portal/disconnect`);
      toast.success("Disconnected");
      setStatus((s) => ({ ...(s || {}), connected: false, last_synced: null }));
      setSnapshot(null);
      setTxn(null);
      await load();
    } catch (err) { toast.error(describeAxiosError(err)); }
  };

  const fileReturn = async () => {
    setBusy("file");
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/portal/file-itr`, { ay, form: snapshot?.itr?.form || "ITR-1", verification: "AADHAAR_OTP" });
      if (data.redirect_url) {
        window.open(data.redirect_url, "_blank", "noopener");
        toast.success("Opening Quicko to complete filing…");
      } else {
        toast.success(`Return ${data.status} — Ack ${data.ack_number}`);
      }
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setBusy(""); }
  };

  const payTax = async () => {
    if (!(Number(payAmt) > 0)) { toast.error("Enter an amount to pay"); return; }
    setBusy("pay");
    try {
      const { data } = await api.post(`/companies/${activeId}/tax/portal/pay-tax`, { ay, kind: payKind, amount: Number(payAmt) });
      if (data.redirect_url) {
        window.open(data.redirect_url, "_blank", "noopener");
        toast.success("Opening Quicko to complete payment…");
      } else {
        toast.success(`${data.status} — CRN ${data.crn} (₹${formatINR(data.amount)})`);
      }
      setPayAmt("");
    } catch (err) { toast.error(describeAxiosError(err)); }
    finally { setBusy(""); }
  };

  const connected = status?.connected;
  const isDemo = (status?.mode || "demo") === "demo";
  const isLive = !isDemo;
  const tp = snapshot?.taxpayer, itr = snapshot?.itr, comp = snapshot?.computation, usr = snapshot?.user;
  // DEMO-only aggregates
  const ais = snapshot?.ais, tis = snapshot?.tis, f26 = snapshot?.form26as, adv = snapshot?.advance_tax;
  const compRows = comp ? COMP_ROWS.filter(([k]) => comp[k] !== undefined && comp[k] !== null) : [];

  return (
    <>
      <PageHeader
        title="Income Tax Portal"
        subtitle="Consent-based fetch of your taxpayer profile, ITR history & tax computation via Quicko Connect — plus continue e-file / e-pay on Quicko. No portal password is stored."
        actions={
          <div className="flex items-center gap-2">
            <TaxYearSelect />
            <Button variant="outline" onClick={() => navigate("/tax")} className="h-8" data-testid="btn-portal-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-6 max-w-5xl">
        {isDemo ? (
          <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 rounded p-3" data-testid="portal-demo-banner">
            <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900">
              <strong>DEMO mode — sample data.</strong> Add live Quicko credentials
              (<code>QUICKO_MODE=live</code> + <code>QUICKO_API_KEY</code> + <code>QUICKO_API_SECRET</code> + <code>QUICKO_TOKEN_ENC_KEY</code>)
              and this connects to the taxpayer's real Quicko account after OTP consent.
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 border border-blue-200 bg-blue-50 rounded p-3" data-testid="portal-live-banner">
            <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-900">
              <strong>Live — Quicko Connect.</strong> The OTP is entered in a secure Quicko pop-up window (this page stays open and your selected company is kept). We then read your
              <strong> taxpayer profile, ITR history and tax computation</strong> (including TDS totals, income by head and deductions). For actual e-filing / tax payment, use
              <strong> Continue on Quicko</strong> below. The taxpayer must be a registered Quicko user.
              <div className="mt-1.5 text-[12px] text-blue-800/90">
                Note: raw <strong>AIS / TIS / Form 26AS</strong> line-items are not available through Quicko's API — those remain on the income-tax portal / Quicko's own app. There is no sanctioned way to log in with your portal password, so AXIORA never asks for or stores it.
              </div>
            </div>
          </div>
        )}

        {/* Connection panel */}
        <Card title="Portal connection" icon={ShieldCheck} testid="portal-connection"
          right={connected ? (
            <Button variant="outline" onClick={disconnect} className="h-8 text-red-600 border-red-200 hover:bg-red-50" data-testid="btn-portal-disconnect">
              <Link2Off className="w-3.5 h-3.5 mr-1" /> Disconnect
            </Button>
          ) : null}
        >
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
            <div><span className="text-gray-500">Provider:</span> <b>{status?.provider || "Quicko Connect"}</b></div>
            <div><span className="text-gray-500">Mode:</span> <b className={isLive ? "text-emerald-700" : "text-amber-700"}>{isLive ? "Live" : "Demo"}</b></div>
            <div><span className="text-gray-500">PAN:</span> <b className="font-mono">{status?.pan || "—"}</b></div>
            <div>
              <span className="text-gray-500">Status:</span>{" "}
              <b className={connected ? "text-emerald-700" : "text-gray-700"} data-testid="portal-conn-state">
                {connected ? "Connected" : "Not connected"}
              </b>
            </div>
            {status?.last_synced && <div><span className="text-gray-500">Last synced:</span> <b>{new Date(status.last_synced).toLocaleString()}</b></div>}
          </div>

          {isLive && status?.redirect_uri && !connected && (
            <div className="mt-3 rounded border border-gray-200 bg-gray-50 px-3 py-2.5" data-testid="portal-redirect-uri">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" /> Whitelist this exact redirect URL in your Quicko Connect app
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-xs font-mono text-gray-800 break-all bg-white border border-gray-200 rounded px-2 py-1.5">{status.redirect_uri}</code>
                <Button variant="outline" onClick={copyRedirect} className="h-8 shrink-0" data-testid="btn-copy-redirect">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                Quicko matches this URL <b>exactly</b> (protocol + path, no trailing slash). Paste it into your Quicko dashboard's redirect / whitelisted-domain field. If you connect from your live domain, the URL above will show that domain automatically.
              </p>
            </div>
          )}

          {!status?.pan && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              No PAN on the company. Add it under <button className="text-blue-700 underline" onClick={() => navigate("/companies")}>Companies</button> first — the portal uses the company's PAN.
            </div>
          )}

          {/* Fetch / consent flow */}
          <div className="mt-4">
            {!txn ? (
              <Button onClick={connected ? fetchDetails : startConsent} disabled={!status?.pan || !!busy} className="h-9 bg-blue-700 hover:bg-blue-800" data-testid="btn-fetch-portal">
                <DownloadCloud className="w-4 h-4 mr-1.5" />
                {connected
                  ? (busy === "fetch" ? "Fetching…" : "Fetch details from income tax portal")
                  : (busy === "start" ? (isLive ? "Redirecting to Quicko…" : "Requesting OTP…") : (isLive ? "Connect & fetch via Quicko" : "Fetch details from income tax portal"))}
              </Button>
            ) : (
              <div className="flex flex-wrap items-end gap-3 border border-dashed border-blue-300 bg-blue-50/40 rounded p-3" data-testid="portal-otp-box">
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wider text-gray-600">Enter OTP</Label>
                  <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit OTP" maxLength={8} className="h-9 w-40 font-mono tracking-widest" data-testid="input-portal-otp" />
                </div>
                <Button onClick={verifyOtp} disabled={busy === "verify"} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-verify-otp">
                  {busy === "verify" ? "Verifying…" : "Verify & fetch"}
                </Button>
                <button onClick={() => { setTxn(null); setOtp(""); }} className="text-xs text-gray-500 hover:underline mb-2">Cancel</button>
              </div>
            )}
          </div>
        </Card>

        {/* ── LIVE snapshot ── */}
        {snapshot && snapshot.live && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="Taxpayer profile" icon={User} testid="card-taxpayer"
                right={usr?.email ? <span className="text-xs text-gray-500 truncate max-w-[180px]">{usr.email}</span> : null}>
                <Row k="Name" v={tp?.full_name || snapshot.name || "—"} strong />
                <Row k="PAN" v={tp?.pan || snapshot.pan || "—"} />
                <Row k="PAN holder type" v={titleCase(tp?.pan_holder_type)} />
                <Row k="Filing category" v={titleCase(tp?.filing_category)} />
                <Row k="Residential status" v={titleCase(tp?.residential_status)} />
                {tp?.contact?.mobile && <Row k="Mobile" v={`${tp.contact.country_code ? "+" + tp.contact.country_code + " " : ""}${tp.contact.mobile}`} />}
                {(tp?.address?.city || tp?.address?.state) && (
                  <Row k="City / State" v={[tp?.address?.city, tp?.address?.state].filter(Boolean).join(", ") || "—"} />
                )}
              </Card>

              <Card title="Latest ITR on record" icon={FileText} testid="card-itr"
                right={itr?.status ? <span className="text-xs font-medium text-emerald-700">{titleCase(itr.status)}</span> : null}>
                {itr ? (
                  <>
                    <Row k="Assessment year" v={itr.assessment_year || `AY ${ay}`} strong />
                    <Row k="Form" v={itr.form || "—"} />
                    <Row k="Filing type" v={titleCase(itr.filing_type)} />
                    <Row k="Filed on" v={dt(itr.filed_on)} />
                    <Row k="Due date" v={dt(itr.due_date)} />
                    <Row k="Acknowledgement no." v={itr.acknowledgement_number || "—"} />
                    <Row k="E-verified on" v={dt(itr.e_verified_on)} />
                    {itr.filed_under_section && <Row k="Filed u/s" v={titleCase(itr.filed_under_section)} />}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No income-tax return found on record via Quicko for this taxpayer yet.</p>
                )}
              </Card>
            </div>

            <Card title="Computation of income & tax" icon={Calculator} testid="card-computation"
              right={comp?.net_tax_payable != null ? <span className="text-xs text-gray-500">Net payable ₹ {formatINR(comp.net_tax_payable)}</span> : null}>
              {compRows.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                  {compRows.map(([k, label, money]) => (
                    <Row key={k} k={label} v={money ? `₹ ${formatINR(comp[k])}` : String(comp[k])} strong={["total_income", "net_tax_payable", "refund", "due"].includes(k)} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No computation available. It appears once a return is prepared/filed on Quicko.</p>
              )}
            </Card>

            {/* File & Pay (continues on Quicko) */}
            <Card title="File Return & Pay Tax" icon={Send} testid="card-file-pay">
              <div className="flex flex-col md:flex-row gap-4 md:items-end">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">e-Filing and tax payment are completed on Quicko's Income-Tax app — your account is already connected.</p>
                  <Button onClick={fileReturn} disabled={!connected || !!busy} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-portal-file">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> {busy === "file" ? "Opening…" : "Continue filing on Quicko"}
                  </Button>
                </div>
                <div className="flex items-end gap-2 border-l border-gray-100 md:pl-4">
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-600">Kind</Label>
                    <select value={payKind} onChange={(e) => setPayKind(e.target.value)} className="h-9 border border-gray-300 rounded px-2 text-sm bg-white" data-testid="select-portal-pay-kind">
                      <option>Advance Tax</option>
                      <option>Self-Assessment Tax</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-600">Amount ₹</Label>
                    <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="h-9 w-32 text-right font-mono" data-testid="input-portal-pay-amount" />
                  </div>
                  <Button onClick={payTax} disabled={!connected || !!busy} className="h-9 bg-blue-700 hover:bg-blue-800" data-testid="btn-portal-pay">
                    <CreditCard className="w-4 h-4 mr-1.5" /> {busy === "pay" ? "Opening…" : "Pay on Quicko"}
                  </Button>
                </div>
              </div>
            </Card>
          </>
        )}

        {/* ── DEMO snapshot ── */}
        {snapshot && snapshot.demo && (
          <>
            <div className="text-[11px] text-amber-700 -mb-2" data-testid="snapshot-demo-flag">Showing DEMO sample data for AY {snapshot.ay}.</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title="Annual Information Statement (AIS)" icon={FileText} testid="card-ais"
                right={<span className="text-xs text-gray-500">Total ₹ {formatINR(ais?.total_reported_income || 0)}</span>}>
                {(ais?.categories || []).map((c, i) => (
                  <Row key={i} k={`${c.category} — ${c.source}`} v={`₹ ${formatINR(c.amount)}`} />
                ))}
              </Card>
              <Card title="Taxpayer Information Summary (TIS)" icon={FileText} testid="card-tis">
                <Row k="Salary" v={`₹ ${formatINR(tis?.salary || 0)}`} />
                <Row k="Interest" v={`₹ ${formatINR(tis?.interest || 0)}`} />
                <Row k="Dividend" v={`₹ ${formatINR(tis?.dividend || 0)}`} />
                <Row k="Capital gains" v={`₹ ${formatINR(tis?.capital_gains || 0)}`} />
                <Row k="Derived total income" v={`₹ ${formatINR(tis?.derived_total || 0)}`} />
              </Card>
              <Card title="Form 26AS" icon={ReceiptIndianRupee} testid="card-26as"
                right={<span className="text-xs text-gray-500">TDS ₹ {formatINR(f26?.total_tds || 0)}</span>}>
                {(f26?.parts || []).map((p, i) => (
                  <Row key={i} k={`Part ${p.part} · ${p.deductor}${p.section ? " · " + p.section : ""}`} v={`₹ ${formatINR(p.tds ?? p.amount ?? 0)}`} />
                ))}
              </Card>
              <Card title="Advance / Self-Assessment Tax Paid" icon={Landmark} testid="card-advance-tax"
                right={<span className="text-xs text-gray-500">Total ₹ {formatINR(adv?.total_paid || 0)}</span>}>
                {(adv?.challans || []).map((c, i) => (
                  <Row key={i} k={`${c.kind} · Challan ${c.challan_no} · ${c.paid_date}`} v={`₹ ${formatINR(c.amount)}`} />
                ))}
              </Card>
              <Card title="Past Returns Filed" icon={FileText} testid="card-returns">
                {(snapshot.returns || []).map((r, i) => (
                  <Row key={i} k={`AY ${r.ay} · ${r.form} · ${r.status}`} v={`Ack ${r.ack_no}`} />
                ))}
              </Card>
              <Card title="Notices & Demand" icon={Bell} testid="card-notices">
                {(snapshot.notices || []).length === 0 ? (
                  <p className="text-xs text-gray-500">No outstanding notices.</p>
                ) : (snapshot.notices || []).map((n, i) => (
                  <div key={i} className="text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <div className="font-medium text-gray-900">{n.type} <span className="text-gray-500 font-normal">· AY {n.ay} · {n.date}</span></div>
                    <div className="text-xs text-gray-600">{n.status}{n.din ? ` · DIN ${n.din}` : ""}</div>
                  </div>
                ))}
              </Card>
            </div>
            <Card title="File Return & Pay Tax" icon={Send} testid="card-file-pay">
              <div className="flex flex-col md:flex-row gap-4 md:items-end">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">E-file this year's return and e-verify, or pay advance / self-assessment tax — all via the portal.</p>
                  <Button onClick={fileReturn} disabled={!connected || !!busy} className="h-9 bg-emerald-700 hover:bg-emerald-800" data-testid="btn-portal-file">
                    <Send className="w-4 h-4 mr-1.5" /> {busy === "file" ? "Filing…" : "File return & e-verify"}
                  </Button>
                </div>
                <div className="flex items-end gap-2 border-l border-gray-100 md:pl-4">
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-600">Kind</Label>
                    <select value={payKind} onChange={(e) => setPayKind(e.target.value)} className="h-9 border border-gray-300 rounded px-2 text-sm bg-white" data-testid="select-portal-pay-kind">
                      <option>Advance Tax</option>
                      <option>Self-Assessment Tax</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] uppercase tracking-wider text-gray-600">Amount ₹</Label>
                    <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} className="h-9 w-32 text-right font-mono" data-testid="input-portal-pay-amount" />
                  </div>
                  <Button onClick={payTax} disabled={!connected || !!busy} className="h-9 bg-blue-700 hover:bg-blue-800" data-testid="btn-portal-pay">
                    <CreditCard className="w-4 h-4 mr-1.5" /> {busy === "pay" ? "Paying…" : "Pay tax"}
                  </Button>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

export default function IncomeTaxPortal() {
  return <RequireCompany><Inner /></RequireCompany>;
}
